import React from 'react';
import { Alert, Button, Card, Descriptions, Empty, Input, Space, Spin, Tag, Typography, message } from 'antd';
import { ExperimentOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useGetConciergePacksQuery,
  useTriggerConciergePackMutation,
  useUpdateAdminOrganizationWebsiteUrlMutation,
} from '../../../services/nestjs-base-api';

const { Text, Paragraph } = Typography;

const STATUS_COLOURS: Record<string, string> = {
  QUEUED_RESEARCH: 'default',
  RESEARCHING: 'processing',
  GENERATING: 'processing',
  DRAFT: 'gold',
  APPROVED: 'blue',
  SENT: 'green',
  KILLED: 'default',
  FAILED: 'red',
  SKIPPED: 'default',
};

interface Props {
  jwt: string;
  organisationId: number;
  // Passed in rather than re-fetched: the page already has the org, and the
  // panel needs it whether or not a pack exists yet.
  websiteUrl?: string | null;
  onWebsiteUrlSaved?: () => void;
}

/**
 * SYR-105 — this org's concierge pack, on the org details page. Reviewing and
 * approving happens on the Concierge queue itself; this is the "does this org
 * have a pack, and where is it up to" view, plus the manual trigger.
 */
export const ConciergePackPanel: React.FC<Props> = ({
  jwt,
  organisationId,
  websiteUrl,
  onWebsiteUrlSaved,
}) => {
  const intl = useIntl();
  const [websiteDraft, setWebsiteDraft] = React.useState('');
  // Two-step confirm for rebuilding after a send. Sending a brand a second
  // pack is not routine, so it should never happen on one stray click.
  const [confirmRebuild, setConfirmRebuild] = React.useState(false);

  // Disarm itself after a few seconds. A destructive button left armed is a
  // trap: come back to the tab later, click once out of habit, and a second
  // pack is queued to a brand that already had one.
  React.useEffect(() => {
    if (!confirmRebuild) return;
    const timer = window.setTimeout(() => setConfirmRebuild(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirmRebuild]);
  const [saveWebsiteUrl, saveWebsiteState] =
    useUpdateAdminOrganizationWebsiteUrlMutation();
  // The list endpoint is small and already cached for the queue page, so
  // filtering here beats adding a per-org endpoint for one card.
  const packsQuery = useGetConciergePacksQuery({ jwtToken: jwt }, { skip: !jwt });
  const [trigger, triggerState] = useTriggerConciergePackMutation();

  const row = (packsQuery.data ?? []).find(
    (candidate) => candidate.pack.organisationId === organisationId,
  );

  const handleSaveWebsite = async () => {
    const value = websiteDraft.trim();
    if (!value) return;
    try {
      const result = await saveWebsiteUrl({
        jwtToken: jwt,
        organizationId: organisationId,
        websiteUrl: value,
      }).unwrap();
      message.success(result.message);
      setWebsiteDraft('');
      onWebsiteUrlSaved?.();
    } catch (error: any) {
      message.error(error?.data?.message || 'Could not save the website URL');
    }
  };

  if (packsQuery.isLoading) return <Spin />;

  return (
    <Card
      title={
        <>
          <ExperimentOutlined style={{ marginRight: 8 }} />
          <FormattedMessage id="concierge.title" />
        </>
      }
      extra={
        <Space>
          {row && (
            <Link to="/admin/crm?tab=concierge">
              <Button size="small">
                <FormattedMessage id="concierge.panel.openQueue" />
              </Button>
            </Link>
          )}
          <Button
            size="small"
            type="primary"
            danger={confirmRebuild}
            icon={<ThunderboltOutlined style={{ marginRight: 4 }} />}
            loading={triggerState.isLoading}
            onClick={async () => {
              const alreadySent = row?.pack.status === 'SENT';
              if (alreadySent && !confirmRebuild) {
                setConfirmRebuild(true);
                return;
              }
              try {
                await trigger({
                  jwtToken: jwt,
                  organisationId,
                  allowAfterSent: confirmRebuild,
                }).unwrap();
                message.success(
                  intl.formatMessage({ id: 'concierge.panel.triggered' }),
                );
              } catch (error: any) {
                message.error(error?.data?.message || 'Failed to queue a pack');
              } finally {
                setConfirmRebuild(false);
              }
            }}
          >
            <FormattedMessage
              id={
                confirmRebuild
                  ? 'concierge.panel.rebuildConfirm'
                  : 'concierge.panel.build'
              }
            />
          </Button>
        </Space>
      }
    >
      {!websiteUrl && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={<FormattedMessage id="concierge.website.missingTitle" />}
          description={
            <>
              <Paragraph style={{ marginBottom: 8 }}>
                <FormattedMessage id="concierge.website.missingBody" />
              </Paragraph>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="https://example.com"
                  value={websiteDraft}
                  onChange={(e) => setWebsiteDraft(e.target.value)}
                  onPressEnter={() => void handleSaveWebsite()}
                />
                <Button
                  type="primary"
                  loading={saveWebsiteState.isLoading}
                  disabled={!websiteDraft.trim()}
                  onClick={() => void handleSaveWebsite()}
                >
                  <FormattedMessage id="concierge.website.save" />
                </Button>
              </Space.Compact>
            </>
          }
        />
      )}

      {!row ? (
        <Empty
          description={intl.formatMessage({ id: 'concierge.panel.none' })}
        />
      ) : (
        <>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label={<FormattedMessage id="concierge.column.status" />}>
              <Tag color={STATUS_COLOURS[row.pack.status] ?? 'default'}>
                {row.pack.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage id="concierge.column.identity" />}>
              <FormattedMessage
                id={`concierge.verification.${row.identityVerificationStatus}`}
              />
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage id="concierge.column.trigger" />}>
              {row.pack.triggerSource}
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage id="concierge.column.checks" />}>
              {row.lintCount > 0 ? (
                <Tag color="red">
                  <FormattedMessage
                    id="concierge.lint.count"
                    values={{ count: row.lintCount }}
                  />
                </Tag>
              ) : (
                <Tag color="green">
                  <FormattedMessage id="concierge.lint.clear" />
                </Tag>
              )}
            </Descriptions.Item>
            {row.pack.sentAt && (
              <Descriptions.Item
                label={<FormattedMessage id="concierge.panel.sent" />}
                span={2}
              >
                {dayjs(row.pack.sentAt).format('D MMM YYYY HH:mm')}
                {row.pack.sentToEmail ? ` — ${row.pack.sentToEmail}` : ''}
              </Descriptions.Item>
            )}
          </Descriptions>

          {row.pack.lastError && (
            <Alert
              style={{ marginTop: 12 }}
              type="error"
              showIcon
              message={<FormattedMessage id="concierge.lastError" />}
              description={row.pack.lastError}
            />
          )}

          {row.pack.status === 'DRAFT' && (
            <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
              <Text type="secondary">
                <FormattedMessage id="concierge.panel.awaitingReview" />
              </Text>
            </Paragraph>
          )}
        </>
      )}
    </Card>
  );
};

export default ConciergePackPanel;
